// Image addresses, and loading images into memory for export. Export draws each frame as an SVG inside an <img>,
// which can't load external files, so every image is fetched once into a data: URL first. Until then, imageSrc()
// gives the plain URL, which is fine on screen.
// Shared art (face sprites, starter places) lives in ../../art, made by scripts/art.ts and scripts/slice.py.
import manifest from "../../art/manifest.json";
import type { Library } from "../model/library";
import { faceSpritesInUse } from "./faceSets";

export type Sprite = { src: string; w: number; h: number };
export const ART = manifest as { bg: Record<string, string>; face: Record<string, Sprite> };

// "face/eyes_open.webp" lives under /art/; library images carry their own absolute path ("/data/v2/images/x.webp").
const versions = new Map<string, number>(); // pictures replaced in place since the page loaded
const url = (src: string) => {
  const u = src.startsWith("/") ? src : `/art/${src}`;
  const v = versions.get(src);
  return v ? `${u}?v=${v}` : u;
};
const inlined = new Map<string, string>();
export const imageSrc = (src: string) => inlined.get(src) ?? url(src);

// A Nano Banana Lite picture comes back at 1K and the server swaps in a sharper copy of the same file a few seconds
// later (src/server/paint.ts; src/ui/api.ts waits for it). Once it has, the picture's address gets a new version so
// the browser fetches it again, and anything already on screen is pointed at it straight away.
export function swapSharper(src: string) {
  const old = imageSrc(src);
  versions.set(src, (versions.get(src) ?? 0) + 1);
  inlined.delete(src);
  loads.delete(src);
  const next = url(src);
  for (const el of document.querySelectorAll("img, image")) {
    const attr = el.tagName.toLowerCase() === "img" ? "src" : "href";
    if (el.getAttribute(attr) === old) el.setAttribute(attr, next);
  }
}

const loads = new Map<string, Promise<void>>();
function inline(src: string) {
  if (!loads.has(src)) loads.set(src, (async () => {
    const res = await fetch(url(src));
    if (!res.ok) throw new Error(`${src}: ${res.status}`);
    const blob = await res.blob();
    inlined.set(src, await new Promise<string>(r => {
      const fr = new FileReader();
      fr.onload = () => r(fr.result as string);
      fr.readAsDataURL(blob);
    }));
  })().catch(e => { loads.delete(src); throw e; }));
  return loads.get(src)!;
}

// Playback swaps big pictures and small face sprites many times a second, so each one must already be fetched and
// decoded, or the faces move on to the new frame while the old picture still shows. The decoded images are kept in
// this map so the browser doesn't drop them from memory. A picture that fails to load is skipped, not fatal.
const decoded = new Map<string, Promise<unknown>>();
export async function decodeImages(srcs: string[]) {
  await Promise.all([...new Set(srcs)].map(src => {
    const href = imageSrc(src);
    if (!decoded.has(href)) {
      const img = new Image();
      img.src = href;
      decoded.set(href, img.decode().then(() => img, () => undefined));
    }
    return decoded.get(href);
  }));
}

// The face sprites in use plus every picture in the library, and any other pictures given (shots).
export function preloadImages(lib: Library, extra: string[] = []) {
  const all = new Set([...faceSpritesInUse(), ...lib.assets.map(a => a.picture.src), ...extra]);
  return Promise.all([...all].map(inline)).then(() => {});
}
